import type { MarkerProps } from '@react-google-maps/api'
import { GoogleMap } from '@/components_deprecated'
import isEqual from 'lodash/isEqual'
import type { Coordinates } from '@/types/graphql/maps'
import React, {
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled, { useTheme } from 'styled-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Icon } from '@ur-solutions/react-components'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);
`

const Thumb = styled.div`
  position: relative;
  height: 150px;
  width: 100%;
  cursor: pointer;
`

const ExpandButton = styled.button`
  position: absolute;
  right: 0.6rem;
  bottom: 0.6rem;
  z-index: 1;

  display: flex;
  align-items: center;
  gap: 0.45rem;

  padding: 0.4rem 0.7rem;
  border: 1px solid #e7e5e4;
  border-radius: 9999px;

  background: white;
  color: #57534e;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #1c1917;
  }
`

interface IssueGoogleMapProps {
  editing: boolean
  interactive: boolean
  latitude: number | null
  longitude: number | null
  borderRadius?: string
  onClick: (
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) => void
}

/**
 * Encapsulates a single GoogleMap instance (center + marker + click-to-geocode).
 * Rendered twice: once as the compact rail preview (non-interactive) and once
 * inside the expand dialog (interactive / editable).
 */
const IssueGoogleMap: React.FC<IssueGoogleMapProps> = ({
  editing,
  interactive,
  latitude,
  longitude,
  borderRadius,
  onClick,
}) => {
  const prevCoordinates = useRef<Coordinates | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [center, setCenter] = useState({
    lat: latitude ?? 63.7196028,
    lng: longitude ?? 11.2339202,
  })

  const handleSetCenter = useEffectEvent((coords: Coordinates) => {
    setCenter(coords)
  })

  useLayoutEffect(() => {
    if (latitude === null || longitude === null) return

    const coordinates = { lat: latitude, lng: longitude }

    if (isEqual(prevCoordinates.current, coordinates)) return
    prevCoordinates.current = coordinates

    handleSetCenter(coordinates)
  }, [latitude, longitude])

  const markers = useMemo<MarkerProps[]>(() => {
    if (
      !mapLoaded ||
      latitude === null ||
      longitude === null ||
      typeof google === 'undefined'
    )
      return []

    return [{ position: new google.maps.LatLng(latitude, longitude) }]
  }, [latitude, longitude, mapLoaded])

  function handleClick(event: google.maps.MapMouseEvent | null) {
    if (!editing || event === null || event.latLng === null) return

    const [lat, lng] = [event.latLng.lat(), event.latLng.lng()]
    const service = new google.maps.Geocoder()
    void service.geocode(
      { location: new google.maps.LatLng(lat, lng) },
      onClick
    )
  }

  return (
    <GoogleMap
      center={center}
      options={{
        streetViewControl: interactive && !editing,
        fullscreenControl: false,
        mapTypeControl: interactive,
        zoomControl: interactive,
        gestureHandling: interactive ? 'greedy' : 'none',
        keyboardShortcuts: interactive,
        clickableIcons: interactive,
        disableDoubleClickZoom: !interactive,
        rotateControl: false,
        tilt: 0,
      }}
      mapContainerStyle={{
        height: '100%',
        ...(borderRadius
          ? {
              borderTopLeftRadius: borderRadius,
              borderTopRightRadius: borderRadius,
            }
          : {}),
      }}
      markers={markers}
      onLoad={() => setMapLoaded(true)}
      onClick={handleClick}
    />
  )
}

interface MapProps {
  className?: string
  editing: boolean

  latitude: number | null
  longitude: number | null

  onClick: (
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) => void
}

export const Map: React.FC<MapProps> = ({
  className,
  editing,
  latitude,
  longitude,
  onClick,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)
  const translations = useTranslate({
    location: 'common.location',
    expand: 'issues.expand-map',
  })

  return (
    <Wrapper className={className}>
      <Thumb onClick={() => setExpanded(true)}>
        <IssueGoogleMap
          editing={false}
          interactive={false}
          latitude={latitude}
          longitude={longitude}
          onClick={onClick}
        />
        <ExpandButton
          type="button"
          onClick={event => {
            event.stopPropagation()
            setExpanded(true)
          }}
        >
          <Icon icon="expand-alt" fixedWidth />
          {translations.expand}
        </ExpandButton>
      </Thumb>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="w-[92vw] sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>{translations.location}</DialogTitle>
          </DialogHeader>
          <div style={{ height: '72vh', width: '100%' }}>
            <IssueGoogleMap
              editing={editing}
              interactive
              latitude={latitude}
              longitude={longitude}
              borderRadius={theme.sizes.defaultBorderRadius}
              onClick={onClick}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Wrapper>
  )
}
